import Vue from "vue";
import Router from "vue-router";
import api from "@/shelves/api";
import LegosHQ from "../components/lego-config/LegosHQ.vue";
import LandingPageWrapper from "@/components/app-stuff/landing/LandingPageWrapper.vue";
import Credentials from "../components/resources/Credentials.vue";
import OrgView from "../components/org-configuration/OrgView.vue";
import ManageOrgsView from "../components/org-configuration/ManageOrgsView.vue";
import HistoryView from "../components/runs/HistoryView.vue";
import SetupOrg from "@/components/org-configuration/SetupOrg.vue";
import WhoopsView from "../components/app-stuff/WhoopsView.vue";
import PageDNEView from "../components/app-stuff/PageDNEView.vue";
import DiagnosticsView from "../components/junk-drawer/DiagnosticsView.vue";
import KitBuilderWrapper from "../components/kit-builder/KitBuilderWrapper.vue";
import Sandflox from "../components/junk-drawer/Sandflox.vue";
import Blank from "../components/junk-drawer/Blank.vue";
import Analytics from "../components/junk-drawer/Analytics.vue";
import UserDrilldown from "../components/org-configuration/UserDrilldown.vue";
import SignupPage from "../components/org-configuration/create-org-view/SignupPage.vue";
import OrgCronJobs from "../components/org-configuration/OrgCronJobs.vue";
import CopyAuthToken from "../components/app-stuff/CopyAuthToken.vue";
import { getAuthInstance } from "@/plugins/auth";
import { store } from "@/plugins/store";
import { oget, nullish } from "@/brains/flang";
import { redToast } from "@/components/comflonents/buefyActions";
import OrgDropdowns from "../components/org-configuration/OrgDropdowns.vue";

Vue.use(Router);
async function waitUntilAuthPluginCreated(to, from, next) {
  console.log("waitUntilAuthPluginCreated");
  const proceedIfAuth = async (auth, _to) => {
    if (!auth.isAuthenticated) {
      console.log("next");
      // this will keep the URL intact, but display the LoginBody if it's a login-protected page
      next();
      return;
    }

    // i.e. if path does not have /:orgSlug/ in it, continue
    if (nullish(oget(_to, "params.orgSlug"))) {
      next();
      return;
    }

    if (!("slugs" in store.state.activeOrg)) {
      // This is only necessary for as long as there are people who
      // have the old org objects cached in their Vuex store
      // Added on 8/5/25. Can be removed after 9/5/25
      const orgs = await api.get("users/me/orgs");
      store.commit("setOrgs", orgs);
      if (store.state.activeOrg) {
        store.commit("setActiveOrgById", store.state.activeOrg.orgId);
      }
    }
    if (store.getters.slugInActiveOrg(_to.params.orgSlug)) {
      if (store.getters.activeOrgActiveSlug == _to.params.orgSlug) {
        next();
      } else {
        next({
          name: _to.name,
          params: { ..._to.params, orgSlug: store.getters.activeOrgActiveSlug },
        });
      }
    } else if (store.getters.slugInSomeOrg(_to.params.orgSlug)) {
      store.commit("setActiveOrgBySlug", _to.params.orgSlug);
      next();
    } else {
      // Maybe a slug has been added and orgs just need to be refreshed?
      const orgs = await api.get("users/me/orgs");
      store.commit("setOrgs", orgs);
      if (store.state.activeOrg) {
        store.commit("setActiveOrgById", store.state.activeOrg.orgId);
      }
      if (store.getters.slugInActiveOrg(_to.params.orgSlug)) {
        if (store.getters.activeOrgActiveSlug == _to.params.orgSlug) {
          next();
        } else {
          next({
            name: _to.name,
            params: {
              ..._to.params,
              orgSlug: store.getters.activeOrgActiveSlug,
            },
          });
        }
      } else if (store.getters.slugInSomeOrg(_to.params.orgSlug)) {
        store.commit("setActiveOrgBySlug", _to.params.orgSlug);
        next();
      } else {
        next("/orgs");
        // TODO: what's a cleaner way to get access to Vue instance from ts files than getAuthInstance()?
        redToast(
          getAuthInstance(),
          `The URL was coded for organization with ID "${_to.params.orgSlug}", but you don't have access to that org`,
          7000
        );
      }
    }
  };

  const auth = getAuthInstance();

  if (!auth.authPluginBeingCreated) {
    await proceedIfAuth(auth, to);
  } else {
    auth.$watch("authPluginBeingCreated", async (authPluginBeingCreated) => {
      if (authPluginBeingCreated === false) {
        await proceedIfAuth(auth, to);
      }
    });
  }
}

function skipLandingIfAuthenticated(to, from, next) {
  console.log("skipLandingIfAuthenticated");
  const redirectIfAuth = (auth) => {
    if (auth.isAuthenticated && store.state.activeOrg) {
      next({
        name: "legosHQRoute",
        params: { orgSlug: store.getters.activeOrgActiveSlug },
      });
    } else if (
      auth.isAuthenticated &&
      process.env.VUE_APP_FLANK_META_ENV != "private_ad"
    ) {
      next({ name: "createOrgRoute" });
    } else {
      next();
    }
  };

  const auth = getAuthInstance();

  if (auth.logoutInProgress) {
    // when we logout, we redirect to the home page before we actually logout
    // and set isAuthenticated to false
    console.log("logout in progress");
    next();
  } else if (!auth.authPluginBeingCreated) {
    console.log("redirectIfAuth");
    redirectIfAuth(auth);
  } else {
    // if auth plugin is being created, wait until it's done
    console.log("watching authPluginBeingCreated");
    auth.$watch("authPluginBeingCreated", (authPluginBeingCreated) => {
      console.log("authPluginBeingCreated changed");
      if (authPluginBeingCreated === false) {
        redirectIfAuth(auth);
      }
    });
  }
}

const beforeEnterChecker = (to, from, next) => {
  if (!(to.meta?.beforeEnterChecks?.responsibleForTurningOffSplash ?? false)) {
    console.log(to.name);
    console.log("setting is loading to false");
    store.commit("setIsLoading", false);
  }
  if (to.meta?.beforeEnterChecks?.waitUntilAuthPluginCreated ?? false) {
    waitUntilAuthPluginCreated(to, from, next);
  } else if (to.meta?.beforeEnterChecks?.skipLandingIfAuthenticated ?? false) {
    skipLandingIfAuthenticated(to, from, next);
  } else {
    next();
  }
};

const router = new Router({
  mode: "history",
  base: process.env.BASE_URL,
  routes: [
    {
      path: "/",
      name: "landingRoute",
      component: LandingPageWrapper,
      meta: {
        viewableWithoutBeingSignedIn: true,
        beforeEnterChecks: { skipLandingIfAuthenticated: true },
        hideNavBar: true,
      },
      beforeEnter: beforeEnterChecker,
    },
    // {
    //   path: "/pricing",
    //   name: "pricing",
    //   component: Pricing,
    //   meta: { viewableWithoutBeingSignedIn: true, title: "Pricing" },
    //   beforeEnter: beforeEnterChecker,
    // },
    {
      path: "/signup",
      name: "signupPage",
      component: SignupPage,
      meta: { viewableWithoutBeingSignedIn: true, title: "Signup" },
      beforeEnter: beforeEnterChecker,
    },

    // ------------------------------------------
    // Core App pages
    // ------------------------------------------
    {
      path: "/:orgSlug/builder",
      name: "kitBuilderRoute",
      component: KitBuilderWrapper,
      props: true,
      meta: {
        viewableWithoutBeingSignedIn: true,
        beforeEnterChecks: { responsibleForTurningOffSplash: true },
      },
      beforeEnter: beforeEnterChecker,
    },
    {
      path: "/:orgSlug/commands",
      name: "legosHQRoute",
      component: LegosHQ,
      props: true,
      meta: {
        beforeEnterChecks: {
          responsibleForTurningOffSplash: true,
          waitUntilAuthPluginCreated: true,
        },
        title: "Commands",
      },
      beforeEnter: beforeEnterChecker,
    },
    {
      path: "/:orgSlug/history",
      name: "historyRoute",
      component: HistoryView,
      props: true,
      meta: {
        beforeEnterChecks: { waitUntilAuthPluginCreated: true },
        title: "History",
      },
      beforeEnter: beforeEnterChecker,
    },
    {
      path: "/:orgSlug/analytics",
      name: "analyticsRoute",
      component: Analytics,
      meta: {
        beforeEnterChecks: { waitUntilAuthPluginCreated: true },
        title: "Analytics",
      },
      beforeEnter: beforeEnterChecker,
    },
    // ------------------------------------------
    // Org settings
    // ------------------------------------------
    {
      path: "/:orgSlug/credentials",
      name: "credentialsRoute",
      component: Credentials,
      meta: {
        beforeEnterChecks: { waitUntilAuthPluginCreated: true },
        title: "Credentials",
      },
      beforeEnter: beforeEnterChecker,
    },
    {
      path: "/:orgSlug/org",
      name: "orgRoute",
      component: OrgView,
      meta: {
        beforeEnterChecks: { waitUntilAuthPluginCreated: true },
        title: "Org",
      },
      beforeEnter: beforeEnterChecker,
    },
    {
      path: "/:orgSlug/members/:userId",
      name: "userDrilldownRoute",
      component: UserDrilldown,
      meta: {
        beforeEnterChecks: { waitUntilAuthPluginCreated: true },
        title: "User",
      },
      beforeEnter: beforeEnterChecker,
    },
    {
      path: "/:orgSlug/cron-jobs",
      name: "orgCronJobsRoute",
      component: OrgCronJobs,
      meta: {
        beforeEnterChecks: { waitUntilAuthPluginCreated: true },
        title: "Scheduled Jobs",
      },
      beforeEnter: beforeEnterChecker,
    },
    {
      path: "/:orgSlug/dropdowns",
      name: "orgDropdownsRoute",
      component: OrgDropdowns,
      meta: {
        beforeEnterChecks: { waitUntilAuthPluginCreated: true },
        title: "Dropdowns",
      },
      beforeEnter: beforeEnterChecker,
    },

    // ------------------------------------------
    // User settings
    // ------------------------------------------
    {
      path: "/orgs",
      name: "manageOrgsRoute",
      component: ManageOrgsView,
      meta: {
        beforeEnterChecks: { waitUntilAuthPluginCreated: true },
        title: "Manage Orgs",
      },
      beforeEnter: beforeEnterChecker,
    },
    {
      path: "/create-org",
      name: "createOrgRoute",
      component: SetupOrg,
      meta: {
        beforeEnterChecks: { waitUntilAuthPluginCreated: true },
        title: "Create Org",
      },
      beforeEnter: beforeEnterChecker,
    },

    // ------------------------------------------
    // Utilities
    // ------------------------------------------
    {
      path: "/copy-token",
      name: "copyTokenRoute",
      component: CopyAuthToken,
      meta: {
        beforeEnterChecks: { waitUntilAuthPluginCreated: true },
        title: "Copy Token",
      },
      beforeEnter: beforeEnterChecker,
    },
    {
      path: "/diagnostics",
      name: "diagnosticsRoute",
      component: DiagnosticsView,
      meta: {
        viewableWithoutBeingSignedIn: true,
        beforeEnterChecks: { waitUntilAuthPluginCreated: true },
        title: "Diagnostics",
      },
      beforeEnter: beforeEnterChecker,
    },
    {
      path: "/sandflox",
      name: "sandfloxRoute",
      component: Sandflox,
      meta: {
        hideNavBar: true,
        viewableWithoutBeingSignedIn: true,
        title: "Sandflox",
      },
      beforeEnter: beforeEnterChecker,
    },
    {
      path: "/loading",
      name: "loadingRoute",
      component: Blank,
      meta: { beforeEnterChecks: { responsibleForTurningOffSplash: true } },
      beforeEnter: beforeEnterChecker,
    },
    {
      path: "/blank",
      name: "blankRoute",
      component: Blank,
      meta: { hideNavBar: true },
      beforeEnter: beforeEnterChecker,
    },
    {
      path: "/whoops",
      name: "whoopsRoute",
      component: WhoopsView,
      props: true,
      meta: { hideNavBar: true, title: "Whoops" },
      beforeEnter: beforeEnterChecker,
    },
    {
      path: "*",
      name: "pageDNERoute",
      component: PageDNEView,
      meta: { title: "Page Not Found" },
      beforeEnter: beforeEnterChecker,
    },
  ],
});

router.afterEach((to, from) => {
  if (to.meta?.title) {
    document.title = to.meta?.title;
  }
});
export default router;
